import ApiService from '@/plugins/api-plugin/services/api.service'
import {
    BranchComparisonDao,
    CiFailureDao,
    ConflictResolutionDao,
    OperationDao,
    OperationHistoryFiltersDao,
    OperationHistoryPageDao,
    OperationHistoryQuery,
    PullRequestCommitDao,
    RepositoryDao,
} from '@/interfaces/admin/ReleaseManager.dao'

export default class ReleaseManagerService extends ApiService {
    async getRepositories(freshCi = false): Promise<RepositoryDao[] | null> {
        return this.request<undefined, RepositoryDao[] | null>(
            'GET',
            '/release-manager/repositories',
            freshCi ? { freshCi: 'true' } : undefined,
            undefined,
            undefined,
            'getRepositories',
        ).catch(() => null)
    }

    async getCachedRepositories(): Promise<RepositoryDao[] | null> {
        return this.request<undefined, RepositoryDao[] | null>(
            'GET',
            '/release-manager/repositories/cached',
            undefined,
            undefined,
            undefined,
            'getCachedRepositories',
        ).catch(() => null)
    }

    async compareBranches(repoName: string): Promise<BranchComparisonDao | null> {
        return this.request<undefined, BranchComparisonDao | null>(
            'POST',
            `/release-manager/repositories/${encodeURIComponent(repoName)}/compare`,
            undefined,
            undefined,
            undefined,
            'compareBranches',
        ).catch(() => null)
    }

    async getPullRequestCommits(repoName: string, pullNumber: number): Promise<PullRequestCommitDao[] | null> {
        return this.request<undefined, PullRequestCommitDao[] | null>(
            'GET',
            `/release-manager/repositories/${encodeURIComponent(repoName)}/pulls/${pullNumber}/commits`,
            undefined,
            undefined,
            undefined,
            'getPullRequestCommits',
        ).catch(() => null)
    }

    async getCiFailure(repoName: string): Promise<CiFailureDao | null> {
        return this.request<undefined, CiFailureDao | null>(
            'GET',
            `/release-manager/repositories/${encodeURIComponent(repoName)}/ci-failure`,
            undefined,
            undefined,
            undefined,
            'getCiFailure',
        ).catch(() => null)
    }

    async startFullMerge(repoName: string): Promise<{ operationId: string } | null> {
        return this.request<undefined, { operationId: string } | null>(
            'POST',
            `/release-manager/repositories/${encodeURIComponent(repoName)}/full-merge`,
            undefined,
            undefined,
            undefined,
            'startFullMerge',
        ).catch(() => null)
    }

    async startCherryPick(repoName: string, prNumbers: number[]): Promise<{ operationId: string } | null> {
        return this.request<{ prNumbers: number[] }, { operationId: string } | null>(
            'POST',
            `/release-manager/repositories/${encodeURIComponent(repoName)}/cherry-pick`,
            undefined,
            { prNumbers },
            undefined,
            'startCherryPick',
        ).catch(() => null)
    }

    async startRevert(repoName: string, prNumbers: number[]): Promise<{ operationId: string } | null> {
        return this.request<{ prNumbers: number[] }, { operationId: string } | null>(
            'POST',
            `/release-manager/repositories/${encodeURIComponent(repoName)}/revert`,
            undefined,
            { prNumbers },
            undefined,
            'startRevert',
        ).catch(() => null)
    }

    async continueOperation(operationId: string, resolution: ConflictResolutionDao): Promise<unknown | null> {
        return this.request<ConflictResolutionDao, unknown>(
            'POST',
            `/release-manager/operations/${operationId}/continue`,
            undefined,
            resolution,
            undefined,
            'continueOperation',
        ).catch(() => null)
    }

    async abortOperation(operationId: string): Promise<unknown | null> {
        return this.request<undefined, unknown>(
            'POST',
            `/release-manager/operations/${operationId}/abort`,
            undefined,
            undefined,
            undefined,
            'abortOperation',
        ).catch(() => null)
    }

    async getOperation(operationId: string): Promise<OperationDao | null> {
        return this.request<undefined, OperationDao | null>(
            'GET',
            `/release-manager/operations/${operationId}`,
            undefined,
            undefined,
            undefined,
            'getOperation',
        ).catch(() => null)
    }

    async listOperations(query: OperationHistoryQuery): Promise<OperationHistoryPageDao | null> {
        const params = new URLSearchParams()
        params.set('page', String(query.page))
        params.set('limit', String(query.limit))
        if (query.repo) params.set('repo', query.repo)
        if (query.userId !== undefined) params.set('userId', String(query.userId))
        if (query.kind) params.set('kind', query.kind)
        if (query.sequenceNumber !== undefined) params.set('sequenceNumber', String(query.sequenceNumber))
        return this.request<undefined, OperationHistoryPageDao | null>(
            'GET',
            `/release-manager/operations?${params.toString()}`,
            undefined,
            undefined,
            undefined,
            'listOperations',
        ).catch(() => null)
    }

    async getOperationFilters(): Promise<OperationHistoryFiltersDao | null> {
        return this.request<undefined, OperationHistoryFiltersDao | null>(
            'GET',
            '/release-manager/operations/filters',
            undefined,
            undefined,
            undefined,
            'getOperationFilters',
        ).catch(() => null)
    }

    async invalidateCache(): Promise<unknown | null> {
        return this.request<undefined, unknown>(
            'POST',
            '/release-manager/invalidate-cache',
            undefined,
            undefined,
            undefined,
            'invalidateCache',
        ).catch(() => null)
    }
}
